import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button, Table, Select } from "antd";
import Layout from "../../Layout";
import CustomerDetailDrawer from "./components/CustomerDetailDrawer";

interface CallItem {
    key: string;
    time: string;
    customerCode: string;
    customerName: string;
    phone: string;
    reason: string;
    reasonTooltip?: boolean;
    staff: string;
    resultCode: string;
    // Dữ liệu dùng để truyền vào Drawer khi click
    serviceCategory?: "Newhair" | "Clinic" | "PTTM" | "Partnership";
    csCode?: string;
}

export default function TongDaiCSKHPage() {
    // State tab đang chọn: "incoming" | "missed" | "history"
    const [activeCallTab, setActiveCallTab] = useState<string>("incoming");

    // State quản lý Drawer chi tiết khách hàng bên phải
    const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);

    // State trạng thái nhân viên CSKH góc dưới
    const [staffStatus, setStaffStatus] = useState<"available" | "break" | "offline">("available");

    // Dữ liệu danh sách Cuộc gọi đến (Chuẩn theo hình ảnh)
    const incomingCalls: CallItem[] = [
        {
            key: "1",
            time: "10:20",
            customerCode: "KH-14421",
            customerName: "Anh L.V.T",
            phone: "098***2204",
            reason: "Có dịch vụ trong N ngày",
            reasonTooltip: true,
            staff: "NV-07",
            resultCode: "CS-01 Ổn định",
            serviceCategory: "Newhair",
            csCode: "CS2"
        },
        {
            key: "2",
            time: "14/10 09:48",
            customerCode: "KH-15890",
            customerName: "Anh P.Q.D",
            phone: "090***8813",
            reason: "Người phụ trách CSKH (R17)",
            reasonTooltip: false,
            staff: "NV-07",
            resultCode: "CS-04 Khiếu nại",
            serviceCategory: "Newhair",
            csCode: "CS2"
        },
    ];

    // Cấu hình các cột của bảng Cuộc gọi
    const columns = [
        {
            title: "Lúc",
            dataIndex: "time",
            key: "time",
            width: "12%",
            render: (text: string) => <span className="text-gray-800 dark:text-gray-200">{text}</span>
        },
        {
            title: "Khách",
            key: "customer",
            width: "25%",
            render: (_: any, record: CallItem) => (
                <span
                    onClick={() => {
                        // Chuyển đổi dữ liệu chuẩn để mở CustomerDetailDrawer
                        setSelectedCustomer({
                            code: record.customerCode,
                            name: record.customerName,
                            phone: record.phone,
                            serviceCategory: record.serviceCategory || "Clinic",
                            csCode: record.csCode || "CS2"
                        });
                    }}
                    className="font-medium text-[#1d4c7d] dark:text-blue-400 cursor-pointer hover:underline"
                >
                    {record.customerCode} · {record.customerName}
                </span>
            ),
        },
        {
            title: "Lý do đổ về CSKH",
            key: "reason",
            width: "38%",
            render: (_: any, record: CallItem) => (
                <div className="flex items-center gap-1">
                    {record.reasonTooltip ? (
                        <div className="border border-amber-500 rounded px-1.5 py-0.5 text-amber-800 dark:text-amber-300 bg-amber-50/50 dark:bg-amber-950/20 font-medium flex items-center gap-1">
                            <span>{record.reason}</span>
                            <span className="bg-[#d97706] text-white text-[10px] px-1 py-0.2 rounded font-bold cursor-pointer">[?]</span>
                        </div>
                    ) : (
                        <span className="text-gray-800 dark:text-gray-200">{record.reason}</span>
                    )}
                </div>
            ),
        },
        {
            title: "Nhân viên",
            dataIndex: "staff",
            key: "staff",
            width: "12%",
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300">{text}</span>
        },
        {
            title: "Mã kết quả",
            dataIndex: "resultCode",
            key: "resultCode",
            width: "13%",
            render: (text: string) => <span className="text-gray-800 dark:text-gray-200 font-medium">{text}</span>
        },
    ];

    const getStatusDotColor = () => {
        switch (staffStatus) {
            case "available": return "#52c41a";
            case "break": return "#d48806";
            case "offline": return "#8c8c8c";
            default: return "#52c41a";
        }
    };

    return (
        <Layout>
            <Helmet>
                <title>Tổng đài CSKH | Khothi</title>
            </Helmet>

            <div className="min-h-screen text-xs flex flex-col justify-between transition-colors duration-200">
                <div className="flex flex-1 overflow-hidden gap-4">
                    
                    {/* KHUNG NỘI DUNG CHÍNH TỔNG ĐÀI BÊN TRÁI */}
                    <div className="space-y-3 overflow-y-auto pb-20 w-full">
                        
                        {/* 1. Header Tiêu đề Tổng đài CSKH */}
                        <div className="flex flex-wrap items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-2 gap-2">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 border-l-4 border-[#1d4c7d] pl-2">
                                <span className="font-bold text-[#1d4c7d] dark:text-blue-400 text-sm">
                                    Tổng đài CSKH
                                </span>
                                <span className="text-gray-600 dark:text-gray-400">
                                    Vai trò: CSKH, Trưởng phòng CSKH <span className="bg-[#d97706] text-white text-[10px] px-1 py-0.2 rounded font-bold cursor-pointer">[?]</span>
                                </span>
                                <span className="text-gray-500 dark:text-gray-400 hidden xl:inline">
                                    Quy tắc: Định tuyến mục 8 · R05 · R15 · R16 · R17 · R22
                                </span>
                            </div>
                        </div>

                        {/* 2. Dòng Khung Cam DỮ LIỆU MINH HOẠ */}
                        <div className="border border-dashed border-orange-400 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 text-center py-1 font-bold tracking-wider rounded text-xs">
                            DỮ LIỆU MINH HOẠ — tên khách, mã nhân viên và mọi con số là giả
                        </div>

                        {/* 3. Thẻ chỉ số KPI (4 Khung Top) */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {/* Cuộc đang chờ */}
                            <div className="bg-white dark:bg-[#1f1f1f] p-3 rounded border border-gray-200 dark:border-gray-800 space-y-1 shadow-sm">
                                <div className="text-gray-500 font-medium">Cuộc đang chờ</div>
                                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">1</div>
                                <div className="text-gray-400 text-[11px]">Chờ lâu nhất 0:22</div>
                            </div>

                            {/* Gọi đến nhỡ chưa gọi lại */}
                            <div className="bg-white dark:bg-[#1f1f1f] p-3 rounded border border-gray-200 dark:border-gray-800 space-y-1 shadow-sm">
                                <div className="text-gray-500 font-medium">Gọi đến nhỡ chưa gọi lại</div>
                                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">1</div>
                                <div className="text-gray-400 text-[11px]">Chờ 25 phút</div>
                            </div>

                            {/* Service Level */}
                            <div className="bg-white dark:bg-[#1f1f1f] p-3 rounded border-2 border-amber-500 space-y-1 shadow-sm">
                                <div className="text-gray-700 dark:text-gray-300 font-medium flex items-center gap-1">
                                    <span>Service Level</span>
                                    <span className="bg-[#d97706] text-white text-[10px] px-1 py-0.2 rounded font-bold cursor-pointer">[?]</span>
                                </div>
                                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">88%</div>
                                <div className="text-gray-400 text-[11px]">15 / 17 cuộc</div>
                            </div>

                            {/* ASA */}
                            <div className="bg-white dark:bg-[#1f1f1f] p-3 rounded border border-gray-200 dark:border-gray-800 space-y-1 shadow-sm">
                                <div className="text-gray-500 font-medium">ASA</div>
                                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">0:14</div>
                                <div className="text-gray-400 text-[11px]">Trả lời trung bình</div>
                            </div>
                        </div>

                        {/* 4. Thanh Chuyển Tab Cuộc gọi */}
                        <div className="border-b border-gray-200 dark:border-gray-700 flex gap-2 text-xs font-semibold pt-2">
                            <button
                                onClick={() => setActiveCallTab("incoming")}
                                className={`px-3 py-1.5 font-bold transition-all rounded-t ${
                                    activeCallTab === "incoming"
                                        ? "bg-[#3E91AB] text-white shadow-sm"
                                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                                }`}
                            >
                                Cuộc gọi đến
                            </button>
                            <button
                                onClick={() => setActiveCallTab("missed")}
                                className={`px-3 py-1.5 font-bold transition-all rounded-t ${
                                    activeCallTab === "missed"
                                        ? "bg-[#3E91AB] text-white shadow-sm"
                                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                                }`}
                            >
                                Cuộc nhỡ
                            </button>
                            <button
                                onClick={() => setActiveCallTab("history")}
                                className={`px-3 py-1.5 font-bold transition-all rounded-t ${
                                    activeCallTab === "history"
                                        ? "bg-[#3E91AB] text-white shadow-sm"
                                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                                }`}
                            >
                                Nhật ký
                            </button>
                        </div>

                        {/* 5. Bảng Danh Sách Cuộc Gọi Đến */}
                        <div className="bg-white dark:bg-[#1f1f1f] rounded border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                            <Table
                                columns={columns}
                                dataSource={incomingCalls}
                                pagination={false}
                                size="small"
                                className="custom-call-center-table"
                                scroll={{ x: "max-content" }}
                                rowClassName={(record, index) =>
                                    index % 2 === 1
                                        ? "bg-slate-50/80 dark:bg-[#252525]"
                                        : "bg-white dark:bg-[#1f1f1f]"
                                }
                            />
                        </div>

                    </div>

                    {/* 6. DRAWER CHI TIẾT KHÁCH HÀNG BÊN PHẢI (MỞ KHI NHẤN TÊN KHÁCH HÀNG) */}
                    {selectedCustomer && (
                        <CustomerDetailDrawer
                            customer={selectedCustomer}
                            onClose={() => setSelectedCustomer(null)}
                        />
                    )}
                </div>

                {/* Floating Widget Thanh Trạng Thái (PC 1 hàng, Mobile tự động xuống 2 hàng) */}
                <div className="sticky bottom-3 z-27 sm:mx-0 bg-[#1d4c7d] dark:bg-[#142f4c] text-white rounded-lg shadow-xl px-3.5 py-2.5 flex flex-col sm:flex-row items-center justify-between sm:justify-start gap-2 sm:gap-3 w-full sm:w-fit border border-[#3E91AB]">
                    
                    {/* Phần thông tin nhân viên và Select trạng thái */}
                    <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-2">
                        <div className="flex items-center gap-2">
                            <span
                                className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                                style={{ backgroundColor: getStatusDotColor() }}
                            ></span>
                            <span className="font-bold text-sm whitespace-nowrap">
                                NV-07 · CSKH
                            </span>
                        </div>

                        {/* Trên mobile hiển thị Select ở đây cho cân đối dòng 1 */}
                        <div className="block sm:hidden">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[110px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>
                    </div>

                    {/* Phần Select (cho PC) và các nút bấm hành động */}
                    <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto border-t border-blue-400/30 pt-2 sm:border-t-0 sm:pt-0">
                        
                        {/* Trên PC hiển thị Select ở vị trí này */}
                        <div className="hidden sm:block">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[125px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>

                        <span className="text-xs text-gray-200 hidden md:inline whitespace-nowrap">
                            Không có cuộc gọi
                        </span>

                        <div className="flex items-center gap-1.5 w-full sm:w-auto justify-end">
                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Mô phỏng gọi đến
                            </Button>

                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Gọi số mới
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}